import { useState, useEffect, Fragment } from 'react';
import { Typography, CircularProgress, Grid, Container, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { PlaybackVideo } from '../../components/PlaybackVideo';
import { useFetchData } from './_hooks/useFetchData';
import { SelectComponent as Select } from '../../components/Select/Select';
import { CollapsibleList } from './CollapsibleList';

export function Script () {
  const [loading, setLoading] = useState(false);
  const [scriptData, setScriptData] = useState<any>(undefined);
  const [selectedValues, setSelectedValues] = useState<{ sequence: string; plane: string; take: string; }>({
    sequence: '',
    plane: '',
    take: '',
  });

  useFetchData(setScriptData, scriptData, setLoading);

  const { recordings } = scriptData || {};

  useEffect(() => {
    if (!recordings) return;

    const { sequence, plane, take } = selectedValues;

    if (!sequence || !plane) {
      if (scriptData.currentRecording) {
        setScriptData((prev: any) => ({...prev, currentRecording: null}));
      }
      return;
    }

    let foundRecording: any = null;
    if (take) {
      foundRecording = recordings.find((r: any) => 
        String(r.sequence) === String(sequence) &&
        String(r.plane) === String(plane) &&
        String(r.take) === String(take)
      );
    } else {
      foundRecording = recordings.find((r: any) => 
        String(r.sequence) === String(sequence) &&
        String(r.plane) === String(plane)
      );
      if (foundRecording) {
        setSelectedValues(prev => ({ ...prev, take: String(foundRecording.take) }));
      }
    }

    if (scriptData.currentRecording?.video_name !== foundRecording?.video_name) {
      setScriptData((prev: any) => ({ ...prev, currentRecording: foundRecording || null }));
    }
  }, [selectedValues, recordings]);

  const handleSelectValue = (key: string, value: string) => {
    if (key === 'sequence') {
      setSelectedValues({ sequence: value, plane: '', take: '' });
    } else if (key === 'plane') {
      setSelectedValues(prev => ({ ...prev, plane: value, take: '' }));
    } else {
      setSelectedValues(prev => ({ ...prev, [key]: value }));
    }
  };

  const planesOptions = selectedValues.sequence && scriptData?.planes
    ? [...(scriptData.planes
      .filter((p: any) => String(p.sequence) === String(selectedValues.sequence))
      .map((p: any) => p.plane))]
    : [];
    
  const takesOptions = selectedValues.sequence && selectedValues.plane && scriptData?.takes
    ? [...(scriptData.takes
      .filter((t: any) => String(t.sequence) === String(selectedValues.sequence) && String(t.plane) === String(selectedValues.plane))
      .map((t: any) => t.take))]
    : [];

  console.log(scriptData);
  return (
    <Container maxWidth="xl">
      {loading ? (
        <Box sx={{ display: 'flex', paddingTop: 1, paddingBottom: 2 }}><CircularProgress sx={{margin: '0 auto', marginTop: 1}} /></Box>
      ) : (
        <Fragment>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={2}>
              <Select options={scriptData?.sequences || []} title="Scene" value={selectedValues.sequence || ''} setValue={(sequence) => handleSelectValue('sequence', sequence)} />
            </Grid>
            <Grid item xs={2}>
              <Select options={planesOptions} title="Shot" value={selectedValues.plane || ''} setValue={(plane) => handleSelectValue('plane', plane)} />
            </Grid>
            <Grid item xs={2}>
              <Select options={takesOptions} title="Take" value={selectedValues.take || ''} setValue={(take) => handleSelectValue('take', take)} />
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
                {scriptData?.currentRecording && (
                  scriptData?.currentRecording?.supervisor_good ? (
                    <Box display="flex" gap={1}>
                      <CheckCircleIcon sx={{ color: 'green' }} />
                      Approved take
                    </Box>  
                  ) : (
                    scriptData?.currentRecording?.supervisor_good === false && (
                      <Box display="flex" gap={1}>
                        <CloseIcon sx={{ color: 'red' }} />
                        Sloppy take
                      </Box>
                    )
                  )
                )}
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={8}>
              {scriptData?.currentRecording && <PlaybackVideo recording={scriptData?.currentRecording} />}
              <Typography>
                <Box mt={2}>
                  {scriptData?.currentRecording && scriptData?.scriptLines.filter((x: any) => x?.covered_by_videos?.includes(scriptData?.currentRecording?.video_name))?.length ? (<strong>Lines covered:</strong>) : ''}
                </Box>
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {scriptData?.currentRecording ? scriptData?.scriptLines.filter((x: any) => x?.covered_by_videos?.includes(scriptData?.currentRecording?.video_name)).flatMap((x: any, index: number) => index !== 0 ? `\n ${x.content}` : x.content) : ''}
                </pre>
              </Typography>
              {!scriptData?.currentRecording && (
                <Typography>
                  Please select a take
                </Typography>
              )}
            </Grid>
            {scriptData?.currentRecording && (
              <Grid item xs={12} md={4}>
              <Box>
                <CollapsibleList
                  title="Shooting Comment"
                  description={scriptData?.currentRecording?.supervisor_comment || 'No shooting comment available.'}
                />
                <CollapsibleList
                  title="Camera Details"
                  items={scriptData?.currentRecording?.CameraInfo ? Object.keys(scriptData?.currentRecording?.CameraInfo || {}).map((key) => ({
                    title: key,
                    value: JSON.stringify(scriptData?.currentRecording.CameraInfo[key]),
                  })) : []}
                  description={scriptData?.currentRecording?.CameraInfo ? undefined : 'No camera details available.'}
                />
                <CollapsibleList
                  title="Description"
                  description={scriptData?.currentRecording?.pegasus_summary?.split(' - SEARCHABLE KEYWORDS:')[0] || 'No description available.'}
                />
              </Box>
            </Grid>
            )}
          </Grid>
        </Fragment>
      )}
      
    </Container>
  )
}
